import { ActionIcon, Pagination, Text } from '@mantine/core';
import {
  IconChevronDown,
  IconChevronRight,
  IconMapPin,
  IconChevronsDown,
  IconChevronsUp,
} from '@tabler/icons-react';
import {
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  OnChangeFn,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AnalysisCommand,
  Order,
  OrderStatus,
} from '../../data-access/types/orders';
import { OrderStatusPill } from '../order-status';
import { OrderTypePill } from '../order-type';

import classes from './timeline-table.module.css';
import { TableRow } from './types';

import { formatDate } from '@data-access';
import { TsrButtonLink } from '@ui';

interface TimelineTableProps {
  pages: number;
  page: number;
  onPageChange: (page: number) => void;
  orders: Order[];
  leftScrollRef: React.RefObject<HTMLTableSectionElement>;
  expandedState: ExpandedState;
  onExpandedStateChange: OnChangeFn<ExpandedState>;
  onLeftTableScroll: (e: React.UIEvent<HTMLTableSectionElement>) => void;
  onScrollToOrder: (order: Order) => void;
  onScrollToAnalysisCommand: (command: AnalysisCommand) => void;
  footerRef: React.RefObject<HTMLTableSectionElement>;
}

export function TimelineTable({
  pages,
  page,
  onPageChange,
  orders,
  leftScrollRef,
  expandedState,
  onExpandedStateChange,
  onLeftTableScroll,
  onScrollToOrder,
  onScrollToAnalysisCommand,
  footerRef,
}: TimelineTableProps) {
  const { t } = useTranslation();

  const handleScrollClick = useCallback(
    (row: TableRow) => {
      if (row.analysis_commands?.length) {
        onScrollToOrder(row as Order);
      } else {
        onScrollToAnalysisCommand(row as AnalysisCommand);
      }
    },
    [onScrollToOrder, onScrollToAnalysisCommand],
  );

  const columns = useMemo<ColumnDef<TableRow>[]>(
    () => [
      {
        id: 'actions',
        header: ({ table }) => {
          const allExpanded = table.getIsAllRowsExpanded();
          return (
            <ActionIcon
              color="dark"
              onClick={table.getToggleAllRowsExpandedHandler()}
              size="sm"
              title={allExpanded ? 'Collapse all' : 'Expand all'}
              variant="subtle"
            >
              {allExpanded ? (
                <IconChevronsUp size={16} />
              ) : (
                <IconChevronsDown size={16} />
              )}
            </ActionIcon>
          );
        },
        cell: ({ row }) => {
          const tableRow = row.original;

          const hasChildren = (tableRow.analysis_commands?.length ?? 0) > 0;

          return (
            <div className={classes.actionButtons}>
              {hasChildren ? (
                <ActionIcon
                  className={classes.expandButton}
                  color="dark"
                  onClick={row.getToggleExpandedHandler()}
                  size="sm"
                  variant="subtle"
                >
                  {row.getIsExpanded() ? (
                    <IconChevronDown size={16} />
                  ) : (
                    <IconChevronRight size={16} />
                  )}
                </ActionIcon>
              ) : (
                <ActionIcon
                  className={classes.scrollButton}
                  color="dark"
                  onClick={() => handleScrollClick(tableRow)}
                  size="sm"
                  title="Scroll to timeline"
                  variant="subtle"
                >
                  <IconMapPin size={16} />
                </ActionIcon>
              )}
            </div>
          );
        },
        size: 50,
      },
      {
        id: 'id',
        accessorFn: (row) => row.id,
        header: t('sampling.identifiant'),
        cell: ({ row }) => {
          const tableRow = row.original;
          if (tableRow.name) {
            return (
              <Text fw={600} size="sm" title={tableRow.name} truncate>
                {tableRow.name}
              </Text>
            );
          } else {
            return (
              <TsrButtonLink
                fullWidth
                justify="start"
                params={{ id: tableRow.id.toString() }}
                size="compact-md"
                td="underline"
                to="/orders/$id/map"
                variant="subtle"
              >
                {tableRow.id}
              </TsrButtonLink>
            );
          }
        },
      },
      {
        id: 'type',
        header: t('sampling.type'),
        cell: ({ row }) => {
          const tableRow = row.original;
          if (tableRow.analysis_type) {
            return <OrderTypePill type={tableRow.analysis_type} />;
          } else {
            return <Text fw="bold">{tableRow.organization?.name || 'OS'}</Text>;
          }
        },
      },
      {
        id: 'laboratory',
        header: t('sampling.laboratoire'),
        cell: ({ row }) => {
          const tableRow = row.original;
          if (tableRow.laboratory?.name) {
            return (
              <Text fw={600} size="sm" title={tableRow.laboratory.name}>
                {tableRow.laboratory.name}
              </Text>
            );
          } else {
            return (
              <Text size="sm">
                {t('sampling.x-orders', {
                  count: tableRow.analysis_commands?.length,
                })}
              </Text>
            );
          }
        },
      },
      {
        id: 'nbr_samples',
        header: t('common.properties.nbr_points'),
        cell: ({ row }) => {
          const tableRow = row.original;
          if (tableRow.nbr_samples) {
            return <Text>{tableRow.nbr_samples}</Text>;
          }
        },
      },
      {
        id: 'nbr_points',
        header: t('common.properties.nbr_samples'),
        cell: ({ row }) => {
          const tableRow = row.original;
          if (tableRow.nbr_points) {
            return <Text>{tableRow.nbr_points}</Text>;
          }
        },
      },
      {
        id: 'sampler',
        header: t('common.preleveur'),
        cell: ({ row }) => {
          const tableRow = row.original;
          if (tableRow.sampler) {
            const samplerName = `${tableRow.sampler.first_name} ${tableRow.sampler.last_name}`;
            return <Text title={samplerName}>{samplerName}</Text>;
          }
        },
      },
      {
        id: 'status',
        header: t('table.columns.status'),
        cell: ({ row }) => {
          const tableRow = row.original;
          if (tableRow.status) {
            return <OrderStatusPill status={tableRow.status as OrderStatus} />;
          }
        },
        size: 150,
      },
      {
        id: 'updated',
        header: t('common.derniere-maj'),
        cell: ({ row }) => {
          const tableRow = row.original;
          if (tableRow.updated_at) {
            return (
              <Text>
                {formatDate({
                  date: tableRow.updated_at,
                  format: 'display-hour',
                })}
              </Text>
            );
          }
        },
        size: 150,
      },
    ],
    [handleScrollClick, t],
  );

  const table = useReactTable({
    data: orders as TableRow[],
    columns,
    state: {
      expanded: expandedState, // must pass expanded state back to the table
    },
    onExpandedChange: onExpandedStateChange,

    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => row.analysis_commands,
    getRowId: (row) => row.id.toString(),
  });

  return (
    <div
      className={classes.container}
      onScroll={onLeftTableScroll}
      ref={leftScrollRef}
    >
      <table className={classes.table}>
        <thead className={classes.headerTable}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr className={classes.headerRow} key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  className={`${classes.headerCell}`}
                  key={header.id}
                  style={{
                    width: header.getSize(),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className={classes.bodyTable}>
          {table.getRowModel().rows.map((row) => {
            const tableRow = row.original;
            return (
              <tr
                className={`${classes.bodyRow} ${
                  tableRow.analysis_commands?.length
                    ? classes.parentRow
                    : classes.childRow
                }`}
                key={row.id}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    className={`${classes.bodyCell}`}
                    key={cell.id}
                    style={{
                      width: cell.column.getSize(),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        <tfoot className={classes.footerTable} ref={footerRef}>
          <tr>
            <td colSpan={columns.length}>
              <Pagination onChange={onPageChange} total={pages} value={page} />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
